import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Typography } from '@dagensmat/carrot/Components';
import { Container as CarrotContainer } from '@dagensmat/carrot/Layout';
import { capitalize } from 'lodash';
import REQ, { ReqType } from 'utils/REQ';
import { formatDate, slugifyDate } from 'utils/date/format';
import { PostOrderResponse, postOrder } from 'api';
import OrderLineTable from '_common/components/orderline-table/OrderLineTable';
import Button, { ButtonLink } from '_common/components/button/Button.style';
import PageHeader from '@components/page-header/PageHeader';
import { useAppSelector } from '_common/hooks/reduxHooks';
import { Consumer } from 'types/Consumer';
import { ProductForSale } from 'types/Product';
import { ProductWithUnit } from '../../add-orderline/ProductList';
import { OrderLine } from '../../../../types/Order';
import { FormField } from '../../../../carrot/form-field';
import { FormFieldType } from '../../../../carrot/form-field/types';
import { SelectedDeliveryDate } from '../../../../_common/components/delivery-date-picker/DeliveryDatePicker';
import Logger from '../../../../services/Logger';
import DeliveryTypeTag from '../../../../_common/components/tags/DeliveryTypeTag';
import { DeliveryType } from '../../../../types/Logistics';
import { useMediaQuery } from '../../../../_common/hooks/useMediaQueries';
import { useGenerateOrderLinesFromProducts } from './useGenerateOrderLinesFromProducts';
import { checkDeliveryRouteReady } from './check-delivery-route-ready';
import { trackOrderFlowPlaceOrder } from './track-order-flow';
import { Page } from '@components/page';
import { BottomActions } from '@components/bottom-actions';
import { CommonNavbar } from '../../../../components/navbar';
import { Box } from '../../../../carrot/layout/box';

type LocationState =
  | {
      consumer: Consumer;
      deliveryDates: SelectedDeliveryDate[];
      products: ProductWithUnit[];
    }
  | undefined;

const AddOrderCheckoutPage = () => {
  const location = useLocation();
  const state = location.state as LocationState;
  const selectedConsumer = state?.consumer;
  const selectedDeliveryDates = state?.deliveryDates ?? [];
  const selectedProducts = state?.products ?? [];

  const [submitReq, setSubmitReq] = useState<ReqType>(REQ.INIT);
  const [createdOrderResponse, setCreatedOrderResponse] =
    useState<PostOrderResponse | null>(null);
  const [ordersByDeliveryDays, setOrdersByDeliveryDays] = useState<
    {
      deliveryDate: string;
      withDagensLogistics: boolean;
      orderReference: string;
      messageFromProducerToConsumer: string;
      consumerId?: string;
      consumerName?: string;
      orderLines: OrderLine[];
    }[]
  >([]);
  const { roleId, userId, products } = useAppSelector(
    ({ auth, producerProducts: { items } }) => {
      return {
        roleId: auth._id,
        userId: auth.uid,
        products: items as unknown as ProductForSale[] // We need to clean up in Product-type, we can't have two who gets used around each other
      };
    }
  );
  const generateOrderLinesFromSelectedProducts =
    useGenerateOrderLinesFromProducts({
      products
    });
  const { t } = useTranslation();
  const { tabletUp } = useMediaQuery();
  const navigate = useNavigate();

  const placeOrders = async () => {
    const orderPayloadsByDeliveryDate = ordersByDeliveryDays.map(
      ({
        deliveryDate,
        withDagensLogistics,
        orderReference,
        messageFromProducerToConsumer,
        consumerId,
        orderLines
      }) => {
        return {
          basket: orderLines.map(({ _key, nrOfUnits }) => {
            return {
              productId: _key,
              unitsInBasket: nrOfUnits
            };
          }),
          deliveryDate: slugifyDate(deliveryDate),
          consumerId,
          createdBy: userId,
          createdAs: roleId,
          messageFromProducerToConsumer,
          withLogistics: withDagensLogistics,
          orderReference
        };
      }
    );
    setSubmitReq(REQ.PENDING);
    try {
      Logger.info(
        `Producer with id ${roleId} creating ${ordersByDeliveryDays.length} order(s) for ${selectedConsumer?.name}`
      );
      const createdOrdersResponse = await Promise.all(
        orderPayloadsByDeliveryDate.map(async ordersByDeliveryDate => {
          Logger.debug(
            `Creating order for delivery date ${ordersByDeliveryDate.deliveryDate}`
          );
          const postOrderRes = await postOrder(ordersByDeliveryDate);
          trackOrderFlowPlaceOrder(
            generateOrderLinesFromSelectedProducts(
              selectedProducts,
              selectedConsumer?._id
            ),
            selectedProducts,
            selectedConsumer?._id,
            selectedConsumer?.name,
            products
          );
          return postOrderRes;
        })
      );
      const createdOrdersResponseFlatten = createdOrdersResponse.flat();
      setCreatedOrderResponse(createdOrdersResponseFlatten);
      await Promise.all(
        await createdOrdersResponseFlatten.map(async ({ orderId }) => {
          await checkDeliveryRouteReady(orderId, 0);
        })
      );
      setSubmitReq(REQ.SUCCESS);
    } catch (error) {
      setSubmitReq(REQ.ERROR);
    }
  };

  const handleMessageFromProducerToConsumerChange = (
    deliveryDate: string,
    message: string
  ) => {
    const updatedOrdersByDeliveryDays = ordersByDeliveryDays.map(
      orderByDeliveryDay => {
        if (orderByDeliveryDay.deliveryDate === deliveryDate) {
          return {
            ...orderByDeliveryDay,
            messageFromProducerToConsumer: message
          };
        }
        return orderByDeliveryDay;
      }
    );
    setOrdersByDeliveryDays(updatedOrdersByDeliveryDays);
  };

  const handleOrderReferenceChange = (
    deliveryDate: string,
    reference: string
  ) => {
    const updatedOrdersByDeliveryDays = ordersByDeliveryDays.map(
      orderByDeliveryDay => {
        if (orderByDeliveryDay.deliveryDate === deliveryDate) {
          return {
            ...orderByDeliveryDay,
            orderReference: reference
          };
        }
        return orderByDeliveryDay;
      }
    );
    setOrdersByDeliveryDays(updatedOrdersByDeliveryDays);
  };

  useEffect(() => {
    const ordersByDeliveryDaysGenerated = selectedDeliveryDates.map(
      selectedDeliveryDate => {
        return {
          deliveryDate: slugifyDate(selectedDeliveryDate.deliveryDate),
          withDagensLogistics: selectedDeliveryDate.withLogistics,
          orderReference: '',
          messageFromProducerToConsumer: '',
          consumerId: selectedConsumer?._id,
          consumerName: selectedConsumer?.name,
          orderLines: generateOrderLinesFromSelectedProducts(
            selectedProducts,
            selectedConsumer?._id
          )
        };
      }
    );
    setOrdersByDeliveryDays(ordersByDeliveryDaysGenerated);
  }, []);

  if (
    !selectedConsumer ||
    selectedDeliveryDates.length === 0 ||
    !selectedProducts
  ) {
    return <Navigate to="/orders/add/start" />;
  }

  if (submitReq === REQ.SUCCESS) {
    setTimeout(() => {
      if (createdOrderResponse?.length === 1) {
        navigate(`/orders/${createdOrderResponse[0].secret}`);
      } else {
        navigate('/orders');
      }
    }, 3000);
  }

  return (
    <Page
      header={
        <>
          <CommonNavbar />
          <Box.FullWidth>
            <PageHeader
              headerText={t('producer:ConfirmTheOrder')}
              subTitle={`${t('common:to')} ${selectedConsumer.name}, ${
                selectedDeliveryDates.length > 1
                  ? `${ordersByDeliveryDays.length} ${t('common:order', {
                      count: ordersByDeliveryDays.length
                    })}`
                  : formatDate(selectedDeliveryDates[0].deliveryDate)
              }`}
              annotation={`${t('common:MakeNewOrder')} (4/4)`}
            />
          </Box.FullWidth>
        </>
      }
      bottom={
        <BottomActions border>
          {(submitReq === REQ.INIT || submitReq === REQ.ERROR) && (
            <>
              <ButtonLink
                to="/orders/add/products"
                state={{
                  consumer: selectedConsumer,
                  deliveryDates: selectedDeliveryDates,
                  products: selectedProducts
                }}
              >
                {t('common:BackWithArrow')}
              </ButtonLink>
              <Button
                onClick={() => {
                  placeOrders();
                }}
                variant="primary"
              >
                {t('producer:PlaceTheOrder', {
                  count: ordersByDeliveryDays.length
                })}
              </Button>
            </>
          )}
          {submitReq === REQ.PENDING && (
            <Typography variant="paragraph" color="secondary">
              {t('producer:PlacingTheOrder', {
                count: ordersByDeliveryDays.length
              })}
            </Typography>
          )}
          {submitReq === REQ.SUCCESS && (
            <Typography variant="paragraph" color="secondary">
              {t('producer:CreateNewOrderOrderCreatedFeedback', {
                count: ordersByDeliveryDays.length
              })}
            </Typography>
          )}
        </BottomActions>
      }
    >
      <CarrotContainer mt="none" mb="m">
        <Typography>
          {t('producer:CreateNewOrderCalloutText', {
            count: ordersByDeliveryDays.length
          })}
        </Typography>
      </CarrotContainer>
      {ordersByDeliveryDays.map(
        ({
          deliveryDate,
          withDagensLogistics,
          orderLines,
          messageFromProducerToConsumer,
          orderReference
        }) => {
          return (
            <CarrotContainer key={deliveryDate} mb="xl">
              <CarrotContainer
                flex
                mb="s"
                gap="xs"
                justify={tabletUp ? 'flex-end' : 'flex-start'}
                alignItems={tabletUp ? 'center' : 'flex-start'}
                direction={tabletUp ? 'row-reverse' : 'column'}
              >
                <DeliveryTypeTag
                  deliveryType={
                    withDagensLogistics
                      ? DeliveryType.DAGENS_DELIVERY
                      : DeliveryType.SELF_DELIVERY
                  }
                />
                <Typography variant="secondaryHeading" my="none">
                  {capitalize(formatDate(deliveryDate))}
                </Typography>
              </CarrotContainer>
              <OrderLineTable
                orderId="no-id-yet"
                orderLines={orderLines}
                isReadOnly
                hideTableHeader
              />
              <CarrotContainer my="m" flex gap="s">
                <FormField
                  type={FormFieldType.TEXT}
                  label={t('producer:MessageToCustomer')}
                  helpText={t('producer:WriteMessageToTheCustomer')}
                  optional
                  inputProps={{
                    value: messageFromProducerToConsumer,
                    onChange: ({ target: { value } }) => {
                      handleMessageFromProducerToConsumerChange(
                        deliveryDate,
                        value
                      );
                    }
                  }}
                />
                <FormField
                  type={FormFieldType.TEXT}
                  label={t('common:OrderReference')}
                  helpText={t('common:OrderReferenceInputHelpText')}
                  optional
                  inputProps={{
                    value: orderReference,
                    onChange: ({ target: { value } }) => {
                      handleOrderReferenceChange(deliveryDate, value);
                    }
                  }}
                />
              </CarrotContainer>
            </CarrotContainer>
          );
        }
      )}
    </Page>
  );
};

export default AddOrderCheckoutPage;
